/**
 * PATH: packages/document-editor/src/useDocumentPagination.ts
 * FILE_ID: DOCEDITOR-PAGINATION-HOOK-001
 * PURPOSE: Measure one ProseMirror flow and map it onto visual A4 pages.
 * WHY: Pagination must preserve native selection, history, and one editor instance.
 * RELATED: pagination.ts, PaginatedPageShell.tsx
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import type { Editor } from '@tiptap/core'
import type { PageSettings } from './contracts'
import {
  calculatePaginationLayout,
  type MeasuredDocumentBlock,
  type PaginationBreak,
} from './pagination'

const CSS_PX_PER_MM = 96 / 25.4
export const PAGE_GAP_PX = 28

function pageDimensions(settings: PageSettings) {
  return settings.orientation === 'portrait'
    ? { widthMm: 210, heightMm: 297 }
    : { widthMm: 297, heightMm: 210 }
}

function blockBreak(element: HTMLElement): PaginationBreak {
  if (element.matches('[data-qdoc-node="page-break"]')) return 'page'
  if (element.matches('[data-qdoc-node="section-break"][data-kind="nextPage"]')) {
    return 'section-next-page'
  }
  return 'none'
}

function measureBlock(element: HTMLElement): MeasuredDocumentBlock {
  const style = getComputedStyle(element)
  const appliedSpacer = Number.parseFloat(
    element.style.getPropertyValue('--qdoc-pagination-spacer'),
  ) || 0
  const margins = Number.parseFloat(style.marginTop) + Number.parseFloat(style.marginBottom)
  return {
    break: blockBreak(element),
    heightPx: element.getBoundingClientRect().height
      + (Number.isFinite(margins) ? Math.max(0, margins - appliedSpacer) : 0),
  }
}

function applyPlacement(element: HTMLElement, page: number, spacerBeforePx: number) {
  element.setAttribute('data-qdoc-page', String(page))
  if (spacerBeforePx <= 0) {
    element.removeAttribute('data-qdoc-page-spacer')
    element.style.removeProperty('--qdoc-pagination-spacer')
    return
  }
  element.setAttribute('data-qdoc-page-spacer', '')
  element.style.setProperty('--qdoc-pagination-spacer', `${spacerBeforePx}px`)
}

export interface DocumentPaginationState {
  pageCount: number
  paperRef: React.RefObject<HTMLDivElement | null>
  viewportRef: React.RefObject<HTMLDivElement | null>
  scrollToPage: (page: number) => void
}

export interface PageCanvasGeometry {
  widthMm: number
  heightMm: number
  scaledWidthPx: number
  scaledHeightPx: number
  paperStyle: CSSProperties
}

export function usePageCanvasGeometry(
  settings: PageSettings,
  zoom: number,
  pageCount: number,
): PageCanvasGeometry {
  const { widthMm, heightMm } = pageDimensions(settings)
  const scale = zoom / 100
  const unscaledHeightPx = pageCount * heightMm * CSS_PX_PER_MM + (pageCount - 1) * PAGE_GAP_PX
  return {
    widthMm,
    heightMm,
    scaledWidthPx: widthMm * CSS_PX_PER_MM * scale,
    scaledHeightPx: unscaledHeightPx * scale,
    paperStyle: {
      width: `${widthMm}mm`,
      minHeight: `${unscaledHeightPx}px`,
      padding: `${settings.marginsMm.top}mm ${settings.marginsMm.right}mm ${settings.marginsMm.bottom}mm ${settings.marginsMm.left}mm`,
      transform: `scale(${scale})`,
      transformOrigin: 'top left',
      '--qdoc-page-height': `${heightMm * CSS_PX_PER_MM}px`,
      '--qdoc-page-gap': `${PAGE_GAP_PX}px`,
    } as CSSProperties,
  }
}

export function useDocumentPagination(
  editor: Editor | null,
  settings: PageSettings,
  zoom: number,
  onActivePageChange: (page: number) => void,
): DocumentPaginationState {
  const paperRef = useRef<HTMLDivElement>(null)
  const viewportRef = useRef<HTMLDivElement>(null)
  const [pageCount, setPageCount] = useState(1)
  const dimensions = pageDimensions(settings)
  const pageHeightPx = dimensions.heightMm * CSS_PX_PER_MM
  const contentHeightPx = (
    dimensions.heightMm - settings.marginsMm.top - settings.marginsMm.bottom
  ) * CSS_PX_PER_MM
  const transitionPx = (
    settings.marginsMm.top + settings.marginsMm.bottom
  ) * CSS_PX_PER_MM + PAGE_GAP_PX

  const measure = useCallback(() => {
    const proseMirror = paperRef.current?.querySelector<HTMLElement>('.ProseMirror')
    if (!proseMirror) return
    const elements = Array.from(proseMirror.children).filter(
      (child): child is HTMLElement => child instanceof HTMLElement,
    )
    const layout = calculatePaginationLayout(
      elements.map(measureBlock),
      { contentHeightPx, pageTransitionPx: transitionPx },
    )
    elements.forEach((element, index) => {
      const placement = layout.placements[index]
      applyPlacement(element, placement.page, placement.spacerBeforePx)
    })
    setPageCount((current) => current === layout.pageCount ? current : layout.pageCount)
  }, [contentHeightPx, transitionPx])

  useLayoutEffect(() => {
    measure()
    const root = paperRef.current
    if (!root || typeof ResizeObserver === 'undefined') return
    let frame = 0
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(measure)
    })
    observer.observe(root)
    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
    }
  }, [editor, measure])

  useEffect(() => {
    if (!editor) return
    editor.on('transaction', measure)
    return () => { editor.off('transaction', measure) }
  }, [editor, measure])

  useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) return
    const handleScroll = () => {
      const stride = (pageHeightPx + PAGE_GAP_PX) * zoom / 100
      const nextPage = Math.min(pageCount, Math.max(1, Math.floor(viewport.scrollTop / stride) + 1))
      onActivePageChange(nextPage)
    }
    viewport.addEventListener('scroll', handleScroll, { passive: true })
    return () => { viewport.removeEventListener('scroll', handleScroll) }
  }, [onActivePageChange, pageCount, pageHeightPx, zoom])

  const scrollToPage = useCallback((page: number) => {
    const bounded = Math.min(pageCount, Math.max(1, page))
    const stride = (pageHeightPx + PAGE_GAP_PX) * zoom / 100
    viewportRef.current?.scrollTo({ top: (bounded - 1) * stride, behavior: 'smooth' })
    onActivePageChange(bounded)
  }, [onActivePageChange, pageCount, pageHeightPx, zoom])

  return { pageCount, paperRef, viewportRef, scrollToPage }
}
